import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from 'auth';
import { I18nService } from 'i18n';
import { of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { MarketingPoolPayload, MarketingPoolService } from './marketing-pool.service';

const CLOSED: MarketingPoolPayload = { visible: false, can_invest: false, referrer: false, can_unlock: false, can_manage: false };

/**
 * `user_payload.marketing_pool` of a user, fail-closed: anything but an explicit
 * `true` is false (other brands' backends do not send the object at all).
 */
export function marketingPoolOf(user: unknown): MarketingPoolPayload {
  const raw = (user as { marketing_pool?: Partial<MarketingPoolPayload> } | null | undefined)?.marketing_pool;
  if (!raw || typeof raw !== 'object') return CLOSED;
  return {
    visible:    raw.visible === true,
    can_invest: raw.can_invest === true,
    referrer:   raw.referrer === true,
    can_unlock: raw.can_unlock === true,
    can_manage: raw.can_manage === true,
    holder:     raw.holder === true
  };
}

/**
 * /:lang/marketing-pool — the member page (spec §1/§14).
 *
 * Fail closed: `marketing_pool.visible` (or `referrer`) from the auth user opens
 * it at once. Without it, the server decides: the holder of existing investments
 * keeps full access after a revoked visibility (§1), and the payload carries no
 * holder flag, so GET /marketing_pool/overview is asked — 200 lets the user in,
 * anything else (404 for everyone else) redirects to the dashboard.
 */
export const marketingPoolGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const i18n = inject(I18nService);
  const service = inject(MarketingPoolService);
  const dashboard = () => router.createUrlTree([`/${i18n.getLanguage() || 'en'}/dashboard`]);

  return auth.getUser$().pipe(
    take(1),
    switchMap(user => {
      if (!user) return of(router.createUrlTree(['/login']));
      const mp = marketingPoolOf(user);
      if (mp.visible || mp.referrer || mp.holder) return of(true);
      return service.overview().pipe(
        map(() => true as const),
        catchError(() => of(dashboard()))
      );
    })
  );
};

/**
 * /:lang/admin/marketing-pool — the admin page. Opened by
 * `marketing_pool.can_manage` (admin, management, any finance right or
 * delegate_unlock — MarketingPoolAccess#can_manage?), deliberately NOT by the
 * shared adminGuard: a management or right holder need not be a platform admin.
 * The page hides tabs the user cannot use; every endpoint checks again (403).
 */
export const marketingPoolAdminGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const i18n = inject(I18nService);

  return auth.getUser$().pipe(
    take(1),
    map(user => {
      if (!user) return router.createUrlTree(['/login']);
      if (marketingPoolOf(user).can_manage) return true;
      return router.createUrlTree([`/${i18n.getLanguage() || 'en'}/dashboard`]);
    })
  );
};
